import type { ComponentChildren } from 'preact';
import { useState } from 'preact/hooks';

/**
 * A box on My work that folds up under its heading (owner 2026-11-02): the heading keeps its count and a short line, so
 * the headings read like status tiles. The choice is remembered in this browser per box; without storage it still
 * works, with the box's default.
 */
const KEY = 'kff.fold';

function readAll(): Record<string, boolean> {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? '{}') as unknown;
    return v && typeof v === 'object' ? (v as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

export function useFold(key: string, openByDefault: boolean): [boolean, () => void] {
  const [open, setOpen] = useState(() => readAll()[key] ?? openByDefault);
  const toggle = () =>
    setOpen((was) => {
      const next = !was;
      try {
        localStorage.setItem(KEY, JSON.stringify({ ...readAll(), [key]: next }));
      } catch {
        /* private window: the choice lasts until the page reloads */
      }
      return next;
    });
  return [open, toggle];
}

/** The box's heading as one button: arrow, title, count, and the short line (shown open or closed). */
export function FoldHead(p: { id: string; title: ComponentChildren; n?: number; tone?: 'crit'; hint?: ComponentChildren; open: boolean; toggle: () => void; children?: ComponentChildren }) {
  return (
    <header class="mw-head fold-head">
      <h2 class="fold-h" id={p.id}>
        <button type="button" class="fold-btn" aria-expanded={p.open} aria-controls={`${p.id}-body`} onClick={p.toggle}>
          <span class="fold-chev" aria-hidden="true" />
          <span class="blk-title">
            {p.title}
            {p.n !== undefined ? <span class={`seg-n${p.tone === 'crit' && p.n ? ' seg-n--crit' : ''}`}>{p.n}</span> : null}
          </span>
          {p.hint ? <span class="small muted fold-hint">{p.hint}</span> : null}
        </button>
      </h2>
      {p.children}
    </header>
  );
}
